/**
 * Nomad profile, reconciled against the reference's own export in
 * `design-reference/` (its verbatim `styles.css` plus every primitive's classes).
 *
 * The reference is dark-first and defines light as an `html.light` override; we
 * are light-only, so the values here are its light block, resolved from oklch().
 *
 * Deliberate divergences, each noted inline at its token:
 *   - type scale follows Nomad text-sm (14px) as the UI/body default
 *   - white on the reference's teal is 2.88:1, so brand ink is picked for AA
 *   - JetBrains Mono on figures is ours; the reference uses Inter + tabular-nums
 */
export const nomadProfile = {
  name: 'nomad',

  /**
   * The reference has one brand hue plus a gradient and a glow. Mapping those
   * onto the three entreprise colours means `--gradient-brand` and the glow can
   * both be rebuilt from the stored settings.
   */
  brand: {
    // --primary / --brand, verbatim
    couleur_primaire: '#00ab9b',
    // The second stop of --gradient-brand
    couleur_secondaire: '#008dbb',
    // --brand-glow
    couleur_accent: '#00c3b2',
  },

  // The active nav item is `bg-sidebar-primary/15`.
  softAlpha: 0.15,

  /**
   * Ink declared on brand fills, bypassing the AA-driven picker in tokens.js.
   *
   * The reference sets `--primary-foreground: #ffffff` on its teal. That pairing
   * measures 2.88:1 — under the 4.5:1 AA minimum for text and under the 3:1 floor
   * for UI components — but it is what Nomad ships, and matching it exactly was
   * an explicit decision. Remove a key here to fall back to the AA-safe pick.
   */
  brandInk: {
    onPrimary: '#ffffff',
  },

  scales: {
    font: {
      // --font-family-sans
      sans: "'Inter', ui-sans-serif, system-ui, sans-serif",
      // --font-family-display, loaded at 500/600/700
      heading: "'Space Grotesk', ui-sans-serif, system-ui, sans-serif",
      /**
       * Figures, dates and amounts — JetBrains Mono (app preference).
       * Nomad reference keeps Inter here; we diverge on purpose.
       */
      mono: "'JetBrains Mono', ui-monospace, 'SFMono-Regular', monospace",
      // The reference's form label is plain sans: 14px/500, foreground, no tracking.
      label: "'Inter', ui-sans-serif, system-ui, sans-serif",
    },
  /**
   * Nomad type scale (design-reference/design-export.json).
   * UI chrome is text-sm (14px); body defaults to the same so unstyled
   * content matches Nomad density. text-base maps to 14px via tokens so
   * Tailwind `text-base` (widely used as “body”) is not the browser 16px.
   */
  fontSize: {
    label: '0.875rem',
    xs: '0.75rem',
    sm: '0.875rem',
    base: '0.875rem',
    md: '1rem',
    lg: '1.125rem',
    xl: '1.25rem',
    '2xl': '1.5rem',
    '3xl': '1.875rem',
  },
    fontWeight: {
      normal: '400',
      medium: '500',
      semibold: '600',
      bold: '700',
      heading: '600',
      // Form labels and table headers are both `font-medium`.
      label: '500',
    },
    lineHeight: {
      tight: '1.25',
      normal: '1.5',
      relaxed: '1.7',
    },
    /**
     * The reference tracks only headings, at -0.02em, and leaves every body and
     * UI size at 0. It has no uppercase or letter-spaced text anywhere, so the
     * tracked label treatment we had was invented; `label` is now flat.
     */
    letterSpacing: {
      tight: '-0.02em',
      normal: '0',
      wide: '0.04em',
      label: '0',
    },
    /**
     * The reference's `--radius: .875rem` expands to sm 10, md 12, lg 14, xl 18
     * and 2xl 22, which map onto xs..xl here. What it reaches for:
     *   xs  menu, dropdown and select items, checkboxes
     *   sm  buttons, inputs, selects, badges, tooltips, popovers, dropdowns
     *   md  tab lists, dialogs, alerts
     *   lg  cards
     */
    radius: {
      xs: '10px',
      sm: '12px',
      md: '14px',
      lg: '18px',
      xl: '22px',
      full: '9999px',
    },
    // Unchanged from Trend: spacing is layout, and no layout value moves.
    space: {
      0: '0px',
      1: '4px',
      2: '8px',
      3: '12px',
      4: '16px',
      5: '20px',
      6: '24px',
      8: '32px',
      10: '40px',
      12: '48px',
    },
    /**
     * The numbered steps are Tailwind's own scale, because that is what the
     * reference's primitives actually ask for: `shadow-sm` on inputs and cards,
     * `shadow-md` on selects and popovers, `shadow-lg` on dialogs and dropdowns.
     *
     * Its soft wide shadows are the three named steps below, and they are opt-in
     * — applied by hand to feature panels and toolbars, not by the primitives.
     * Using them everywhere is what made our surfaces look heavier than Nomad's.
     */
    shadow: {
      xs: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
      sm: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1)',
      md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
      lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)',
      xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
      // --shadow-card
      card: '0 8px 32px -12px rgba(17, 22, 31, 0.12)',
      // --shadow-elevated
      elevated: '0 20px 60px -20px rgba(17, 22, 31, 0.18)',
    },
    // The reference's cool neutrals, ordered by lightness. 500 is its --border
    // base, 700 its --muted-foreground and 900 its --foreground.
    gray: {
      50: '#ffffff',
      100: '#f6f9fc',
      200: '#f0f2f5',
      300: '#ebeff5',
      400: '#dfe5ef',
      500: '#c7cbd1',
      600: '#999fa8',
      700: '#6b727e',
      800: '#323842',
      900: '#0c121a',
    },
    /**
     * Base colours are the reference's exactly. The `*Ink` variants are darkened
     * so they clear AA as text on their own tint, which is how Tag, Badge and
     * Alert use them; the undarkened base stays the fill, dot and border colour.
     */
    semantic: {
      error: '#d73240',
      errorInk: '#ca2f3c',
      errorBg: '#fbebec',
      errorBorder: '#f4c6ca',
      warning: '#cd8800',
      warningInk: '#986500',
      warningBg: '#faf3e6',
      warningBorder: '#f1deb8',
      warningOrange: '#cd8800',
      warningOrangeBg: '#faf3e6',
      warningOrangeBorder: '#f1deb8',
      success: '#00a159',
      successInk: '#007e45',
      successBg: '#e6f6ee',
      successBorder: '#b8e5d1',
      info: '#1f86cd',
      infoInk: '#1b73b0',
      infoBg: '#e9f3fa',
      infoBorder: '#c0ddf1',
    },
    surface: {
      // --background
      page: '#f6f9fc',
      canvas: '#f6f9fc',
      // --card / --popover
      card: '#ffffff',
      /**
       * The reference's `.panel` and `.glass` are frosted: the card colour at 70%
       * over a blurred backdrop, hairlined with the foreground at 8%.
       */
      cardGlass: 'rgba(255, 255, 255, 0.7)',
      glassBorder: 'rgba(12, 18, 26, 0.08)',
      // --sidebar
      sidebar: '#f0f2f5',
      // --overlay
      overlay: 'rgba(17, 22, 31, 0.45)',
    },
    text: {
      // --foreground, which the reference sets on html/body and on headings
      dark: '#0c121a',
      heading: '#0c121a',
      body: '#0c121a',
      // --muted-foreground; 4.85:1 on white, so it clears AA as body text
      muted: '#6b727e',
      light: '#999fa8',
      inverse: '#ffffff',
    },
    // The reference leans on translucent rings rather than solid borders.
    border: {
      default: 'rgba(199, 203, 209, 0.6)',
      subtle: 'rgba(199, 203, 209, 0.35)',
      strong: '#c7cbd1',
    },
    /**
     * The reference's input is `border border-input bg-transparent shadow-sm`:
     * an unfilled box with a hairline, not a tinted fill. `--input` is its border
     * colour, not its background — reading it as a background is what made our
     * fields look like grey chips. Focus is a one-pixel ring in `--ring`, on top
     * of the global two-pixel `:focus-visible` outline.
     */
    field: {
      bg: 'transparent',
      border: 'rgba(218, 222, 229, 0.8)',
      borderFocus: '#008a7c',
      ring: '#008a7c',
    },
    /**
     * `h-9` is the reference's control height, `h-8` and `h-10` its small and
     * large. Inputs are `px-3 py-1`; buttons are wider at `px-4`, which is why
     * button padding is tracked separately.
     */
    control: {
      height: '36px',
      heightSm: '32px',
      heightLg: '40px',
      paddingX: '12px',
      paddingY: '4px',
      buttonPaddingX: '16px',
      buttonPaddingXSm: '12px',
      buttonPaddingXLg: '32px',
    },
    /**
     * Shell and page metrics. The reference centres a 1400px column inside a page
     * padded 16px on mobile and 24px from `md` up, under a 56px header, beside a
     * 256px sidebar that collapses to a 56px icon rail.
     */
    layout: {
      pagePadding: '24px',
      pagePaddingMobile: '16px',
      contentMaxWidth: '1400px',
      sectionGap: '24px',
      cardPadding: '24px',
      headerHeight: '56px',
      sidebarWidth: '300px',
      sidebarWidthCollapsed: '56px',
      sidebarWidthMobile: '320px',
      tableRowHeight: '48px',
    },
    duration: {
      fast: '120ms',
      // The reference transitions chrome at 200ms.
      normal: '200ms',
      slow: '280ms',
    },
    easing: {
      standard: 'cubic-bezier(0.4, 0, 0.2, 1)',
      decelerate: 'cubic-bezier(0, 0, 0.2, 1)',
    },
    zIndex: {
      base: '1',
      sticky: '100',
      header: '1100',
      popup: '1200',
      modal: '2000',
      toast: '2100',
    },
  },

  /**
   * Table and drawer chrome is neutral. The reference's `<thead>` has no fill at
   * all — it is a bottom border with `font-medium text-muted-foreground` cells —
   * so the header background is the card colour rather than a tinted band.
   */
  buildComponent: ({ primary, secondary, accent, scales, withAlpha }) => ({
    tableHeaderBg: scales.surface.card,
    tableHeaderColor: scales.text.muted,
    tableBodyBg: scales.surface.card,
    drawerHeaderBg: scales.surface.card,
    footerBg: scales.surface.card,
    // Nomad CTA: fixed brand gradient + glow (design oklch), not rebuilt from hex.
    gradientBrand: 'linear-gradient(135deg, oklch(65% 0.15 185), oklch(58% 0.16 220))',
    glowShadow: '0 0 50px -10px oklch(65% 0.15 185 / 0.3)',
    // --gradient-glow, the radial brand wash behind the page.
    pageGlow: `radial-gradient(600px circle at 20% 0%, ${withAlpha(primary, 0.1)}, transparent 50%)`,
  }),
}

export default nomadProfile
